import { Box, Button, Card, Grid, Icon, Paper } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { connect } from "react-redux";
import { NavLink, useParams, useRouteMatch } from "react-router-dom";
import AppBreadCrumb from "../../../../components/AppBreadCrumb/AppBreadCrumb";
import ConfirmationDialog from "../../../../utilities/components/ConfirmationDialog/ConfirmationDialog";
import ImageField from "../../../../utilities/components/ImageField/ImageField";
import { APP_URLS } from "../../../../utilities/constants";
import { dataURLtoFile } from "../../../../utilities/utilityFunctions";
import {
  AutocompleteValidator,
  PhoneInputValidator,
} from "../../../../utilities/validators";
import UtilHelper from "../../../../utilities/validators/UtilHelper";
import {
  addOrganisationRequest,
  getOrgByIdRequest,
  getOrgSubTypesRequest,
  getOrgTypesRequest,
  getReadReceiptStatusRequest,
  getSignedUrlRequest,
  updateOrgRequest,
  uploadToS3Request,
} from "../../OrganisationsApiActions";
import { useStyles } from "./styles";

/**
 * function to render organization form
 * @param location: location from router
 * @param history: history from router
 * @param getOrgTypes: function to get organization types
 * @param organizationTypeData: function to get organization sub types
 * @param getOrgSubTypes: organization types list
 * @param organizationSubtypeData: organization sub types list
 * @param getReadReceiptStatus: function to get read receipt status
 * @param readReceiptStatusData: read receipt status
 * @param addOrganisation: function to add organization
 * @param getOrgById: function to get organization by id
 * @param updateOrg: function to update existing organization
 * @param getSignedUrl: function to get signed url
 * @param uploadToS3: function to upload file on signed url
 * @returns {JSX.Element}
 * @constructor
 */
const OrganizationForm = ({
  location,
  history,
  getOrgTypes,
  getOrgSubTypes,
  getReadReceiptStatus,
  readReceiptStatusData,
  addOrganisation,
  getOrgById,
  updateOrg,
  getSignedUrl,
  uploadToS3,
}) => {
  const headOrgId = history?.location?.state?.headOrgId;

  const classes = useStyles();
  const params = useParams();
  const match = useRouteMatch();
  const currentUrlPath = match.path.split(":")[0];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerContact, setOwnerContact] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [address, setAddress] = useState("");
  const [organizationType, setOrganizationType] = useState(null);
  const [organizationSubtype, setOrganizationSubtype] = useState(null);
  const [readReceiptStatus, setReadReceiptStatus] = useState(null);
  const [reAuthentication, setReAuthentication] = useState(null);
  const [reAuthenticationTime, setReAuthenticationTime] = useState("");
  const [requireLogin, setRequireLogin] = useState("");
  const [promptForPINTouchID, setPromptForPINTouchID] = useState("");
  const [openCancelConfirmation, setOpenCancelConfirmation] = useState(false);
  const [rowImageFile, setRowImageFile] = useState(null);
  const [cropper, setCropper] = useState(null);
  const [croppedImageFile, setCroppedImageFile] = useState(null);
  const [showImageEditor, setShowImageEditor] = useState(false);

  useEffect(() => {
    ValidatorForm.addValidationRule("isBlank", (value) =>
      UtilHelper.validateBlankSpace(value)
    );
    ValidatorForm.addValidationRule("isValidMobile", (value) =>
      UtilHelper.validateMobile(value)
    );
    ValidatorForm.addValidationRule("isMaxLength", (value) =>
      UtilHelper.validateMaxLength(value)
    );

    return () => {
      ValidatorForm.removeValidationRule("isBlank");
      ValidatorForm.removeValidationRule("isValidMobile");
      ValidatorForm.removeValidationRule("isMaxLength");
    };
  }, []);

  useEffect(() => {
    if (!params.id) {
      getReadReceiptStatus().then((res) => {});
    }
  }, [getReadReceiptStatus, params.id]);

  useEffect(() => {
    if (!params.id) {
      getOrgTypes().then((res) => {});
    }
  }, [getOrgTypes, params.id]);

  useEffect(() => {
    if (params.id) {
      getOrgById(params.id).then((res) => {
        getReadReceiptStatus().then((resRss) => {
          getOrgTypes().then((resOrgType) => {
            const type = resOrgType.find((type) => type.id === res.typeId);
            const rrs = resRss.find((rrs) => rrs.id === res.readReceiptStatus);

            if (type && rrs) {
              getOrgSubTypes(type.id).then((resSub) => {
                const subtype = resSub.find(
                  (subtype) => subtype.id === res.subTypeId
                );

                setOrganizationType(type);
                setOrganizationSubtype(subtype);
                setReadReceiptStatus(rrs);

                setCroppedImageFile(res.imageUrl);
                setName(res.name);
                setDescription(res.desc);
                setOwnerName(res.ownerName);
                setOwnerEmail(res.ownerEmail);
                setOwnerContact(res.ownerContact);
                setAddress(res.address);
                setRequireLogin(res.requireLogin.toString());
                setPromptForPINTouchID(res.detectSecurity.toString());
                if (res.reauthenticationType) {
                  setReAuthentication(res.reauthenticationType.toString());
                }
                setReAuthenticationTime(
                  res.reauthenticationValue ? res.reauthenticationValue : null
                );
              });
            }
          });
        });
      });
    }
  }, [
    params.id,
    getOrgById,
    getOrgSubTypes,
    getReadReceiptStatus,
    getOrgTypes,
  ]);

  const breadCrumbDataForAddOrgForm = [
    {
      icon: "business",
      name: "Clinics",
      path: APP_URLS.ORGANIZATIONS,
    },
    {
      name: `Add Clinic`,
      path: APP_URLS.ORGANIZATIONS_ADD,
    },
  ];

  const breadCrumbDataForUpdateOrgForm = [
    {
      icon: "business",
      name: "Clinics",
      path: APP_URLS.ORGANIZATIONS,
    },
    {
      name: `Update Clinic`,
      path: `${APP_URLS.ORGANIZATIONS_EDIT}/${params.id}`,
    },
  ];

  /**
   * function to get breadcrumb data for current url
   */
  const getBreadcrumbData = (urlPath) => {
    switch (urlPath) {
      case APP_URLS.ORGANIZATIONS_ADD: {
        return breadCrumbDataForAddOrgForm;
      }
      case `${APP_URLS.ORGANIZATIONS_EDIT}/`: {
        return breadCrumbDataForUpdateOrgForm;
      }
      default: {
        return [];
      }
    }
  };

  /**
   * function to handle image crop
   */
  const onImageCrop = () => {
    const croppedImageDataUrl = cropper.getCroppedCanvas().toDataURL();
    const croppedImageFileObject = dataURLtoFile(
      croppedImageDataUrl,
      rowImageFile[0].file.name
    );

    const data = {
      dataURL: croppedImageDataUrl,
      file: croppedImageFileObject,
    };

    setCroppedImageFile(data);
    setShowImageEditor(false);
  };

  /**
   * function to add org
   * @param e: event object on form submission
   */
  const onAdd = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name,
        desc: description,
        ownerName,
        ownerEmail,
        ownerContact,
        address,
        typeId: 1,
        subTypeId: 1,
        readReceiptStatus: readReceiptStatus.id,
        reauthenticationType: reAuthentication,
        reauthenticationValue: reAuthenticationTime,
        requireLogin: requireLogin === "true",
        detectSecurity: promptForPINTouchID === "true",
      };

      if (croppedImageFile) {
        const { name: fileName } = croppedImageFile.file;
        const fileType = `.${fileName.split(".").pop()}`;

        const res = await getSignedUrl({ fileName, fileType });
        await uploadToS3(res.urls, croppedImageFile.file);

        data.imageUrl = res.path;
      }

      const response = await addOrganisation(data, headOrgId);
      history.push(`${APP_URLS.ORGANIZATIONS}/${response.id}`);
    } catch (error) {
      console.error("Error while adding organisation:", error);
    }
  };

  /**
   * function to update org
   * @param e: event object on form submission
   */
  const onUpdate = async (e) => {
    e.preventDefault();

    try {
      let imageUrl = null;

      if (typeof croppedImageFile !== "string") {
        const { name: fileName } = croppedImageFile.file;
        const fileType = `.${fileName.split(".").pop()}`;

        const res = await getSignedUrl({ fileName, fileType });
        await uploadToS3(res.urls, croppedImageFile.file);
        imageUrl = res.path;
      }

      const data = {
        name,
        desc: description,
        ownerName,
        ownerEmail,
        ownerContact,
        address,
        typeId: organizationType.id,
        subTypeId: organizationSubtype.id,
        readReceiptStatus: readReceiptStatus.id,
        reauthenticationType: reAuthentication,
        reauthenticationValue: reAuthenticationTime,
        requireLogin: requireLogin === "true",
        detectSecurity: promptForPINTouchID === "true",
        ...(imageUrl && { imageUrl }),
      };

      const updatedOrg = await updateOrg(params.id, data);
      history.push({
        pathname: `${APP_URLS.ORGANIZATIONS}/${params.id}`,
        state: { updatedOrg },
      });
    } catch (error) {
      console.error("Error updating organization:", error);
    }
  };

  /**
   * function to handle cancel add or update
   */
  const handleCancel = () => {
    history.goBack();
  };

  return (
    <Box className={classes.outerContainer}>
      <AppBreadCrumb>
        {getBreadcrumbData(currentUrlPath).map((crumb, index) => (
          <Box
            key={index}
            component={NavLink}
            exact
            to={{
              pathname: crumb.path,
              state: {
                orgIdHistory: location.state && location.state.orgIdHistory,
                orgNameHistory: location.state && location.state.orgNameHistory,
              },
            }}
            className={classes.breadcrumbItem}
            activeClassName={classes.activeBreadcrumbItem}
          >
            {crumb.icon && (
              <Icon className={classes.breadcrumpIcon}>{crumb.icon}</Icon>
            )}
            <Box className={classes.breadcrumpLabel}>{crumb.name}</Box>
          </Box>
        ))}
      </AppBreadCrumb>

      <Box mt={2}>
        <ValidatorForm onSubmit={params.id ? onUpdate : onAdd}>
          <Box className={classes.addOragnizationContainer}>
            <Box className={classes.addOragnizationFormContainer}>
              <Box
                component={Paper}
                className={classes.addOragnizationFormInnerContainer}
              >
                <Grid container spacing={2} justify="space-around">
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <Box>
                      <ImageField
                        rowImageFile={rowImageFile}
                        setRowImageFile={setRowImageFile}
                        showImageEditor={showImageEditor}
                        setShowImageEditor={setShowImageEditor}
                        setCropper={setCropper}
                        croppedImageFile={croppedImageFile}
                        onImageCrop={onImageCrop}
                        options={{
                          shape: "rect",
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <TextValidator
                          label="Clinic Branch Name *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          validators={["required", "isBlank", "isMaxLength"]}
                          errorMessages={[
                            "Clinic branch name is required",
                            "Please enter Clinic branch name",
                            "Maximum 100 letters are accepted.",
                          ]}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <TextValidator
                          label="Description *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          validators={["required", "isBlank", "isMaxLength"]}
                          errorMessages={[
                            "Description is required",
                            "Please enter description",
                            "Maximum 100 letters are accepted.",
                          ]}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <AutocompleteValidator
                          fullWidth
                          size="small"
                          value={readReceiptStatus}
                          onChange={(e, value) => {
                            setReadReceiptStatus(value);
                          }}
                          options={
                            readReceiptStatusData ? readReceiptStatusData : []
                          }
                          getOptionLabel={(option) => option.description}
                          getOptionSelected={(option, value) =>
                            option.id === value.id
                          }
                          renderInput={(params) => (
                            <TextValidator
                              {...params}
                              label="Read Receipt Status *"
                              variant="outlined"
                            />
                          )}
                          validators={["required"]}
                          errorMessages={["Read receipt status is required"]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <TextValidator
                          label="Branch admin name *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={ownerName}
                          onChange={(e) => setOwnerName(e.target.value)}
                          validators={["required", "isBlank", "isMaxLength"]}
                          errorMessages={[
                            "Branch admin name is required",
                            "Please enter Branch admin name",
                            "Maximum 100 letters are accepted.",
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <PhoneInputValidator
                          autoFormat={false}
                          variant="outlined"
                          size="small"
                          label="Branch admin Contact *"
                          fullWidth
                          defaultCountry="in"
                          countryCodeEditable={false}
                          value={ownerContact}
                          onChange={(phone) => {
                            let phoneNumber =
                              "+" + phone.replace(/[^0-9]+/g, "");
                            setOwnerContact(phoneNumber);
                          }}
                          validators={["required", "isValidMobile"]}
                          errorMessages={[
                            "Branch admin contact is required",
                            "Please enter valid contact number",
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <TextValidator
                          label="Branch admin email *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={ownerEmail}
                          onChange={(e) => setOwnerEmail(e.target.value)}
                          validators={["required", "isEmail"]}
                          errorMessages={[
                            "Branch admin email is required",
                            "Please enter valid branch admin email",
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <TextValidator
                          label="Address *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          validators={["required", "isBlank", "isMaxLength"]}
                          errorMessages={[
                            "Address is required",
                            "Please enter Address",
                            "Maximum 100 letters are accepted.",
                          ]}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>

          <Box className={classes.footerContainer}>
            <Box
              component={Card}
              raised
              className={classes.footerContainerInner}
            >
              <Box>
                <Button
                  variant="outlined"
                  color="primary"
                  disableElevation={true}
                  onClick={() => setOpenCancelConfirmation(true)}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  disableElevation={true}
                  type="submit"
                  className={classes.saveButton}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        </ValidatorForm>
      </Box>

      <Box>
        <ConfirmationDialog
          open={openCancelConfirmation}
          setOpen={setOpenCancelConfirmation}
          title="Cancel"
          message="Are you sure you want to cancel?"
          onConfirm={handleCancel}
        />
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    organizationTypeData: state.organisations.orgTypes,
    organizationSubtypeData: state.organisations.orgSubTypes,
    readReceiptStatusData: state.organisations.readReceiptStatus,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addOrganisation: (data, headOrgId) => {
      return dispatch(addOrganisationRequest(data, headOrgId)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getOrgById: (id) => {
      return dispatch(getOrgByIdRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    updateOrg: (id, data) => {
      return dispatch(updateOrgRequest(id, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getOrgTypes: () => {
      return dispatch(getOrgTypesRequest()).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getOrgSubTypes: (id) => {
      return dispatch(getOrgSubTypesRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getReadReceiptStatus: () => {
      return dispatch(getReadReceiptStatusRequest()).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getSignedUrl: (data) => {
      return dispatch(getSignedUrlRequest(data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    uploadToS3: (url, file) => {
      return dispatch(uploadToS3Request(url, file)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationForm);
